<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useRouter } from 'vue-router'
import { api, getToken, setToken } from '@/api/client'
import { EP } from '@/api/endpoints'
import { useCartStore } from '@/stores/cartStore'
import { useToastStore } from '@/stores/toastStore'
import { useCurrencyStore } from '@/stores/currencyStore'
import IconUser from '@/design/icons/IconUser.vue'
import IconMenu from '@/design/icons/IconMenu.vue'
import IconClose from '@/design/icons/IconClose.vue'
import IconCart from '@/design/icons/IconCart.vue'
import CoinGlyph from '@/design/primitives/CoinGlyph.vue'
import TopUpModal from '@/components/TopUpModal.vue'

const route    = useRoute()
const router   = useRouter()
const cart     = useCartStore()
const toast    = useToastStore()
const currency = useCurrencyStore()
const drawerOpen = ref(false)
const balance = ref(null)
const activityTokens = ref(0)
const withdrawalsEnabled = ref(true)

// Top-up modal — opened from header button or global 'open-topup' event
const topUpOpen = ref(false)
function openTopUp() {
  if (!getToken()) {
    router.push('/register?next=/profile')
    return
  }
  topUpOpen.value = true
}
function onOpenTopUp() { openTopUp() }

// ── Currency switcher ──────────────────────────────────────────────────────
const currencyMenuOpen = ref(false)
const currencyAnchor   = ref(null)

function toggleCurrencyMenu() {
  if (!currency.hasChoice) return
  currencyMenuOpen.value = !currencyMenuOpen.value
}
function pickCurrency(code) {
  currency.select(code)
  currencyMenuOpen.value = false
}
function onDocClick(e) {
  if (!currencyMenuOpen.value) return
  if (currencyAnchor.value && !currencyAnchor.value.contains(e.target)) {
    currencyMenuOpen.value = false
  }
}
function onEsc(e) {
  if (e.key === 'Escape') currencyMenuOpen.value = false
}
onMounted(() => {
  currency.load()
  currency.loadAvailable()
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onEsc)
  window.addEventListener('open-topup', onOpenTopUp)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onEsc)
  window.removeEventListener('open-topup', onOpenTopUp)
})

async function signOut() {
  try { await api.post(EP.authLogout) } catch {}
  setToken(null)
  balance.value = null
  toast.show('Signed out.', { tone: 'success', duration: 2500 })
  drawerOpen.value = false
  router.push('/')
}

const navItems = [
  { to: '/',        label: 'Home' },
  { to: '/market',  label: 'Store' },
  { to: '/profile', label: 'Account' }
]

const isAuthed = computed(() => balance.value !== null)

async function loadUser() {
  // Fast-path: if there's no token in localStorage, don't even ask.
  if (!getToken()) {
    balance.value = null
    return
  }
  try {
    const { data } = await api.get(EP.authUser)
    const u = data?.user || data
    balance.value = (u && u.balance != null) ? u.balance : null
    activityTokens.value = (u && u.activity_tokens != null) ? u.activity_tokens : 0
  } catch {
    balance.value = null
    activityTokens.value = 0
  }
}

onMounted(loadUser)
// Re-fetch on every route change so login / register / logout flows
// stay in sync with the header's authed state.
watch(() => route.fullPath, loadUser)

// Fetch withdrawal config so the AT pill hides when admin disabled payouts.
onMounted(async () => {
  try {
    const { data } = await api.get('/api/withdrawals/config')
    withdrawalsEnabled.value = data?.enabled !== false
  } catch {
    withdrawalsEnabled.value = false
  }
})
</script>

<template>
  <header class="hdr">
    <div class="hdr__inner">
      <RouterLink to="/" class="hdr__brand" aria-label="Evoluxu home">
        <span class="hdr__brand-plate">EVOLUXU</span>
      </RouterLink>

      <nav class="hdr__nav" aria-label="primary">
        <RouterLink
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          class="hdr__link"
          :class="{ 'hdr__link--active': route.path === item.to || (item.to !== '/' && route.path.startsWith(item.to)) }"
        >{{ item.label }}</RouterLink>
      </nav>

      <div class="hdr__right">
        <div
          v-if="currency.available.length > 0"
          ref="currencyAnchor"
          class="hdr__cur-wrap"
        >
          <button
            type="button"
            class="hdr__cur"
            :class="{ 'hdr__cur--open': currencyMenuOpen, 'hdr__cur--single': !currency.hasChoice }"
            :aria-haspopup="currency.hasChoice ? 'menu' : undefined"
            :aria-expanded="currency.hasChoice ? String(currencyMenuOpen) : undefined"
            :aria-label="`Currency, ${currency.code}${currency.hasChoice ? '. Click to change.' : ''}`"
            @click.stop="toggleCurrencyMenu"
          >{{ currency.code }}</button>

          <Transition name="curmenu">
            <ul
              v-if="currencyMenuOpen"
              class="hdr__cur-menu"
              role="menu"
              :aria-label="'Select currency'"
            >
              <li
                v-for="c in currency.available"
                :key="c.code"
                role="menuitemradio"
                :aria-checked="c.code === currency.code"
                class="hdr__cur-opt"
                :class="{ 'hdr__cur-opt--active': c.code === currency.code }"
                @click="pickCurrency(c.code)"
              >
                <span class="hdr__cur-opt-sym">{{ c.symbol }}</span>
                <span class="hdr__cur-opt-code">{{ c.code }}</span>
                <span class="hdr__cur-opt-name">{{ c.name }}</span>
                <span v-if="c.code === currency.code" class="hdr__cur-opt-tick" aria-hidden="true">✓</span>
              </li>
            </ul>
          </Transition>
        </div>

        <RouterLink to="/cart" class="hdr__cart" :aria-label="`Cart, ${cart.count} item${cart.count === 1 ? '' : 's'}`">
          <IconCart class="hdr__icon"/>
          <span v-if="cart.count > 0" class="hdr__cart-badge">{{ cart.count }}</span>
        </RouterLink>
        <button v-if="isAuthed" class="hdr__balance hdr__balance--btn" type="button" aria-label="Top up balance" @click="openTopUp">
          <CoinGlyph :size="18"/>
          <span class="hdr__balance-num">{{ balance }}</span>
        </button>
        <RouterLink
          v-if="isAuthed && withdrawalsEnabled"
          to="/profile?tab=withdrawals"
          class="hdr__balance hdr__balance--tokens"
          title="Activity Tokens"
          aria-label="Activity Tokens balance"
        >
          <span class="hdr__tokens-icon" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="16" height="16"><circle cx="8" cy="8" r="6.5" fill="currentColor" opacity=".18"/><circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M5.5 8 L7.2 9.7 L10.6 6.3" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          <span class="hdr__balance-num">{{ activityTokens }}</span>
          <span class="hdr__tokens-unit">AT</span>
        </RouterLink>
        <RouterLink v-if="!isAuthed" to="/register" class="hdr__signin">
          <IconUser class="hdr__icon"/>
          <span>Sign in</span>
        </RouterLink>
        <button class="hdr__burger" aria-label="Open menu" @click="drawerOpen = true">
          <IconMenu class="hdr__icon-lg"/>
        </button>
      </div>
    </div>

    <Transition name="drawer">
      <div v-if="drawerOpen" class="drawer">
        <button class="drawer__close" aria-label="Close menu" @click="drawerOpen = false">
          <IconClose class="hdr__icon-lg"/>
        </button>
        <nav class="drawer__nav">
          <RouterLink
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            class="drawer__link"
            @click="drawerOpen = false"
          >{{ item.label }}</RouterLink>

          <div v-if="currency.hasChoice" class="drawer__cur" role="radiogroup" aria-label="Currency">
            <p class="drawer__cur-label u-display u-allcaps">Currency</p>
            <div class="drawer__cur-row">
              <button
                v-for="c in currency.available"
                :key="c.code"
                type="button"
                role="radio"
                :aria-checked="c.code === currency.code"
                class="drawer__cur-chip"
                :class="{ 'drawer__cur-chip--active': c.code === currency.code }"
                @click="pickCurrency(c.code)"
              >{{ c.code }}</button>
            </div>
          </div>

          <button v-if="isAuthed" class="drawer__signout" @click="signOut">Sign out</button>
        </nav>
      </div>
    </Transition>

    <!-- Global top-up modal -->
    <TopUpModal :open="topUpOpen" @close="topUpOpen = false" />
  </header>
</template>

<style scoped>
.hdr {
  position: relative;
  z-index: 50;
  background: transparent;
  border: 0;
  pointer-events: none;
}
.hdr__inner {
  height: var(--header-h);
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: 0 var(--sp-6);
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--sp-6);
  pointer-events: auto;
}

/* Logo — gold stamped plate with "EVOLUXU" inside.
   Same metallic aesthetic as the EvoGems E-coin, applied to the full wordmark. */
.hdr__brand {
  display: inline-flex;
  align-items: center;
  height: 100%;
}
.hdr__brand-plate {
  display: inline-block;
  height: 38px;
  line-height: 38px;          /* vertically centers Anton baseline in the box */
  padding: 0 16px;
  font-family: var(--font-display);
  font-size: 22px;
  letter-spacing: 0.22em;
  text-indent: 0.22em;        /* offsets right-side letter-spacing for h-center */
  text-align: center;
  color: var(--ink-inverse);
  border-radius: var(--r-xs);
  border: 1px solid #8C6E2A;
  background: linear-gradient(180deg, #F5DC97 0%, #E5C76B 55%, #C19F4F 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.35),
    inset 0 -1px 0 rgba(0, 0, 0, 0.18),
    0 2px 8px rgba(0, 0, 0, 0.45);
  transition: filter var(--t-fast) var(--ease-road);
}
.hdr__brand:hover .hdr__brand-plate { filter: brightness(1.08); }

.hdr__nav { display: flex; gap: var(--sp-8); justify-content: center; }
.hdr__link {
  color: var(--ink);
  font-family: var(--font-display);
  font-size: var(--fs-14);
  text-transform: uppercase;
  letter-spacing: 0.20em;
  padding: 8px 0;
  transition: color var(--t-fast) var(--ease-road), opacity var(--t-fast) var(--ease-road);
  opacity: 0.85;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.55);
}
.hdr__link:hover { color: var(--gold); opacity: 1; }
.hdr__link--active { color: var(--gold); opacity: 1; }

.hdr__right { display: flex; align-items: center; gap: var(--sp-2); }
.hdr__balance, .hdr__signin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  height: 38px;
  padding: 0 14px;
  border-radius: var(--r-xs);
  border: 1px solid var(--ash-strong);
  background: rgba(11, 10, 8, 0.55);
  font-family: var(--font-display);
  font-size: var(--fs-12);
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: var(--ink);
  transition: border-color var(--t-fast) var(--ease-road), color var(--t-fast) var(--ease-road), background var(--t-fast) var(--ease-road);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}
.hdr__balance:hover, .hdr__signin:hover {
  border-color: var(--gold);
  color: var(--gold);
  background: rgba(11, 10, 8, 0.78);
}
.hdr__balance-num { font-family: var(--font-mono); color: var(--gold); letter-spacing: 0.04em; }

/* Balance is rendered as a <button> so click opens the top-up modal — strip default button styling */
.hdr__balance--btn {
  cursor: pointer;
  font-family: inherit;
}

/* Activity Tokens balance — navigates to profile, no top-up "+" affordance */
.hdr__balance--tokens {
  color: var(--gold);
  text-decoration: none;
  border-color: rgba(229, 199, 107, 0.35);
}
.hdr__balance--tokens .hdr__balance-num { color: var(--gold); }
.hdr__balance--tokens:hover {
  border-color: var(--gold);
  color: var(--gold);
  background: var(--gold-soft);
}
.hdr__tokens-icon {
  width: 16px;
  height: 16px;
  display: inline-flex;
  color: var(--gold);
}
.hdr__tokens-unit {
  font-size: 0.65rem;
  letter-spacing: 0.16em;
  color: var(--gold);
  opacity: 0.7;
}

.hdr__icon { width: 16px; height: 16px; }
.hdr__icon-lg { width: 22px; height: 22px; }

/* Currency switcher — same 38x38 shape as cart, but holds 3-letter code */
.hdr__cur-wrap {
  position: relative;
  display: inline-flex;
}
.hdr__cur {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--r-xs);
  border: 1px solid var(--ash-strong);
  background: rgba(11, 10, 8, 0.55);
  color: var(--ink);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  font-family: var(--font-display);
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0;
  transition: border-color var(--t-fast) var(--ease-road),
              color        var(--t-fast) var(--ease-road),
              background   var(--t-fast) var(--ease-road);
}
.hdr__cur:hover,
.hdr__cur--open {
  border-color: var(--gold);
  color: var(--gold);
  background: rgba(11, 10, 8, 0.78);
}
.hdr__cur--single {
  cursor: default;
}
.hdr__cur--single:hover {
  /* Don't fake interactivity when there's only one currency */
  border-color: var(--ash-strong);
  color: var(--ink);
  background: rgba(11, 10, 8, 0.55);
}

/* Currency dropdown menu — anchored under the button, right-aligned */
.hdr__cur-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 200px;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  background: var(--bg-glass-strong);
  border: 1px solid var(--ash-strong);
  border-radius: var(--r-sm);
  box-shadow: var(--shadow-2);
  backdrop-filter: blur(10px) saturate(140%);
  -webkit-backdrop-filter: blur(10px) saturate(140%);
  z-index: 70;
}
.hdr__cur-opt {
  display: grid;
  grid-template-columns: 24px 48px 1fr auto;
  align-items: center;
  gap: var(--sp-2);
  padding: 8px 14px;
  cursor: pointer;
  font-size: var(--fs-12);
  letter-spacing: 0.04em;
  color: var(--ink);
  transition: background var(--t-fast) var(--ease-road),
              color      var(--t-fast) var(--ease-road);
}
.hdr__cur-opt:hover {
  background: rgba(229, 199, 107, 0.08);
  color: var(--gold);
}
.hdr__cur-opt--active {
  color: var(--gold);
}
.hdr__cur-opt-sym {
  font-family: var(--font-display);
  font-size: var(--fs-16);
  text-align: center;
  color: var(--gold);
}
.hdr__cur-opt-code {
  font-family: var(--font-display);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.hdr__cur-opt-name {
  font-family: var(--font-body);
  color: var(--ink-soft);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hdr__cur-opt--active .hdr__cur-opt-name { color: var(--ink); }
.hdr__cur-opt-tick {
  font-family: var(--font-mono);
  color: var(--gold);
}

.curmenu-enter-active,
.curmenu-leave-active {
  transition: opacity var(--t-fast) var(--ease-cinema),
              transform var(--t-fast) var(--ease-cinema);
}
.curmenu-enter-from,
.curmenu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Cart icon button — same shape as signin/burger (38x38 box) */
.hdr__cart {
  position: relative;
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--r-xs);
  border: 1px solid var(--ash-strong);
  background: rgba(11, 10, 8, 0.55);
  color: var(--ink);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  transition: border-color var(--t-fast) var(--ease-road), color var(--t-fast) var(--ease-road), background var(--t-fast) var(--ease-road);
}
.hdr__cart:hover {
  border-color: var(--gold);
  color: var(--gold);
  background: rgba(11, 10, 8, 0.78);
}
.hdr__cart .hdr__icon { width: 18px; height: 18px; }
.hdr__cart-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  color: var(--ink-inverse);
  background: var(--gold);
  border-radius: 9px;
  border: 1px solid #8C6E2A;
  letter-spacing: 0;
  line-height: 1;
}

.hdr__burger {
  display: none;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  color: var(--ink);
  border-radius: var(--r-xs);
  background: rgba(11, 10, 8, 0.55);
  border: 1px solid var(--ash-strong);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  padding: 0;
}

@media (max-width: 860px) {
  /* Switch from 3-col grid to flex so brand sits left and right-block pins right */
  .hdr__inner {
    display: flex;
    justify-content: space-between;
    gap: var(--sp-3);
    padding: 0 var(--sp-4);
  }
  .hdr__nav { display: none; }
  .hdr__burger { display: inline-flex; width: 38px; height: 38px; }
  .hdr__brand-plate { font-size: 16px; padding: 0 12px; height: 38px; line-height: 38px; letter-spacing: 0.18em; }
  /* Profile/sign-in becomes a square 38x38 icon button — matches burger + plate */
  .hdr__signin, .hdr__balance {
    width: 38px;
    height: 38px;
    padding: 0;
    gap: 0;
  }
  .hdr__signin span { display: none; }
  .hdr__balance-num { display: none; }
}

.drawer {
  position: fixed;
  inset: 0;
  background: rgba(11, 10, 8, 0.97);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  z-index: 60;
  padding: var(--sp-6);
  display: flex;
  flex-direction: column;
  gap: var(--sp-12);
  pointer-events: auto;
}
.drawer__close {
  align-self: flex-end;
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ink);
  border-radius: var(--r-xs);
  border: 1px solid var(--ash-strong);
  background: rgba(11, 10, 8, 0.55);
}
.drawer__close:hover { color: var(--gold); border-color: var(--gold); }
.drawer__nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-6);
  text-align: center;
}
.drawer__link {
  font-family: var(--font-display);
  font-size: var(--fs-32);
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--ink);
}
.drawer__link:hover { color: var(--gold); }
.drawer__signout {
  font-family: var(--font-display);
  font-size: var(--fs-21);
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--ember);
  background: none;
  padding: 0;
  margin-top: var(--sp-6);
  align-self: center;
}
.drawer__signout:hover { color: var(--ink); }

/* Currency picker inside the mobile drawer */
.drawer__cur {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-3);
  margin-top: var(--sp-4);
}
.drawer__cur-label {
  margin: 0;
  font-size: var(--fs-12);
  letter-spacing: 0.16em;
  color: var(--ink-soft);
}
.drawer__cur-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--sp-2);
}
.drawer__cur-chip {
  min-width: 56px;
  height: 38px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink);
  background: rgba(11, 10, 8, 0.55);
  border: 1px solid var(--ash-strong);
  border-radius: var(--r-xs);
  transition: border-color var(--t-fast) var(--ease-road),
              color        var(--t-fast) var(--ease-road),
              background   var(--t-fast) var(--ease-road);
}
.drawer__cur-chip:hover {
  border-color: var(--gold);
  color: var(--gold);
}
.drawer__cur-chip--active {
  border-color: var(--gold);
  color: var(--ink-inverse);
  background: var(--gold);
}
.drawer-enter-active, .drawer-leave-active { transition: opacity var(--t-base) var(--ease-cinema); }
.drawer-enter-from, .drawer-leave-to { opacity: 0; }
</style>
